
import { storage } from './firebase';
import { ref, uploadString, getDownloadURL, listAll, deleteObject } from 'firebase/storage';

/**
 * Checks if a string is a base64 image data URI (not an SVG, which is small enough to inline).
 */
const isBase64Image = (str: string): boolean => {
    if (!str || typeof str !== 'string') return false;
    return (str.startsWith('data:image/') && !str.startsWith('data:image/svg+xml')) ||
           // Raw base64 (no prefix) that's large enough to be an image (>1KB)
           (/^[A-Za-z0-9+/=]{1000,}$/.test(str));
};

/**
 * Uploads a base64 image to Firebase Storage and returns the public URL.
 * @param base64Data The base64 encoded image data (with or without data:image/png;base64, prefix)
 * @param path The storage path (e.g., 'itineraries/tripId/header.png')
 */
export const uploadImageToStorage = async (base64Data: string, path: string): Promise<string | null> => {
    if (!storage) {
        console.warn("Storage not initialized. Returning base64 as fallback.");
        return base64Data;
    }

    // If it's an SVG data URI, just return it directly. It's tiny and doesn't need storage.
    if (base64Data.startsWith('data:image/svg+xml')) {
        return base64Data;
    }

    try {
        // Remove prefix if present
        const base64Content = base64Data.includes('base64,')
            ? base64Data.split('base64,')[1]
            : base64Data;

        const storageRef = ref(storage, path);

        // Race upload against a 45s timeout to prevent vault save from hanging
        const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Upload timed out after 45s')), 45000)
        );
        await Promise.race([
            uploadString(storageRef, base64Content, 'base64'),
            timeoutPromise
        ]);

        const downloadUrl = await getDownloadURL(storageRef);
        return downloadUrl;
    } catch (error) {
        console.error("Storage: Upload failed for", path, error);
        // Return null on failure so the app can fallback to using the base64 string in local state
        return null;
    }
};

/**
 * Walks through trip data, uploads any base64 images to Firebase Storage,
 * and replaces them with download URLs. Returns the modified trip data
 * safe for Firestore storage (no large base64 strings).
 *
 * @param tripId The Firestore document ID for the trip
 * @param tripData The full trip data object (may contain base64 image strings)
 * @param userId Optional user ID for user-scoped storage paths
 * @returns Modified trip data with base64 images replaced by Storage URLs
 */
export const uploadTripImages = async (
    tripId: string,
    tripData: any,
    userId?: string
): Promise<any> => {
    if (!storage || !tripData) return tripData;

    const basePath = userId
        ? `users/${userId}/itineraries/${tripId}`
        : `itineraries/${tripId}`;

    // Deep clone to avoid mutating the original
    const data = JSON.parse(JSON.stringify(tripData));
    const uploadPromises: Promise<void>[] = [];

    // 1. Header image
    if (isBase64Image(data.headerImageUrl)) {
        uploadPromises.push(
            uploadImageToStorage(data.headerImageUrl, `${basePath}/header.png`)
                .then(url => { if (url) data.headerImageUrl = url; })
        );
    }

    // 2. Itinerary day images
    if (Array.isArray(data.itinerary)) {
        data.itinerary.forEach((day: any, i: number) => {
            if (isBase64Image(day.imageUrl)) {
                uploadPromises.push(
                    uploadImageToStorage(day.imageUrl, `${basePath}/day${day.day || i + 1}.png`)
                        .then(url => { if (url) day.imageUrl = url; })
                );
            }
        });
    }

    // 3. Logistics images (flight, hotel)
    if (data.logistics) {
        if (isBase64Image(data.logistics.flight?.imageUrl)) {
            uploadPromises.push(
                uploadImageToStorage(data.logistics.flight.imageUrl, `${basePath}/flight.png`)
                    .then(url => { if (url) data.logistics.flight.imageUrl = url; })
            );
        }
        if (isBase64Image(data.logistics.hotel?.imageUrl)) {
            uploadPromises.push(
                uploadImageToStorage(data.logistics.hotel.imageUrl, `${basePath}/hotel.png`)
                    .then(url => { if (url) data.logistics.hotel.imageUrl = url; })
            );
        }

        // Flight alternatives
        if (Array.isArray(data.logistics.flightAlternatives)) {
            data.logistics.flightAlternatives.forEach((alt: any, i: number) => {
                if (isBase64Image(alt.imageUrl)) {
                    uploadPromises.push(
                        uploadImageToStorage(alt.imageUrl, `${basePath}/flight-alt-${i}.png`)
                            .then(url => { if (url) alt.imageUrl = url; })
                    );
                }
            });
        }

        // Hotel alternatives
        if (Array.isArray(data.logistics.hotelAlternatives)) {
            data.logistics.hotelAlternatives.forEach((alt: any, i: number) => {
                if (isBase64Image(alt.imageUrl)) {
                    uploadPromises.push(
                        uploadImageToStorage(alt.imageUrl, `${basePath}/hotel-alt-${i}.png`)
                            .then(url => { if (url) alt.imageUrl = url; })
                    );
                }
            });
        }

        // Airbnbs
        if (Array.isArray(data.logistics.airbnbs)) {
            data.logistics.airbnbs.forEach((listing: any, i: number) => {
                if (isBase64Image(listing.imageUrl)) {
                    uploadPromises.push(
                        uploadImageToStorage(listing.imageUrl, `${basePath}/airbnb-${i}.png`)
                            .then(url => { if (url) listing.imageUrl = url; })
                    );
                }
            });
        }
    }

    // 4. UserInput fields (customLogo, travelerPhoto)
    if (data.userInput) {
        if (isBase64Image(data.userInput.customLogo)) {
            uploadPromises.push(
                uploadImageToStorage(data.userInput.customLogo, `${basePath}/logo.png`)
                    .then(url => { if (url) data.userInput.customLogo = url; })
            );
        }
        if (isBase64Image(data.userInput.travelerPhoto)) {
            uploadPromises.push(
                uploadImageToStorage(data.userInput.travelerPhoto, `${basePath}/traveler.jpg`)
                    .then(url => { if (url) data.userInput.travelerPhoto = url; })
            );
        }
    }

    // Run all uploads concurrently
    await Promise.allSettled(uploadPromises);

    return data;
};

/**
 * Deletes all images stored under a trip's folder in Firebase Storage.
 *
 * @param tripId The Firestore document ID for the trip
 * @param userId Optional user ID for user-scoped storage paths
 */
export const deleteTripImages = async (tripId: string, userId?: string): Promise<void> => {
    if (!storage) return;

    const basePath = userId
        ? `users/${userId}/itineraries/${tripId}`
        : `itineraries/${tripId}`;

    try {
        const folderRef = ref(storage, basePath);
        const result = await listAll(folderRef);

        if (result.items.length === 0) {
            return;
        }

        const deletePromises = result.items.map(itemRef =>
            deleteObject(itemRef).catch(err =>
                console.warn(`Storage: Failed to delete ${itemRef.fullPath}`, err)
            )
        );

        await Promise.allSettled(deletePromises);
    } catch (error) {
        console.error(`Storage: Failed to delete images for trip ${tripId}`, error);
    }
};
